import { baseApi } from "./baseApi";
import {
  clearStoredAdminUser,
  getStoredAdminUser,
  saveStoredAdminUser,
  subscribeToAdminAuth,
} from "../auth/authSession";
import type {
  AdminSession,
  AuthResponse,
  LoginPayload,
  TransformedAuthResponse,
  UpdateAdminProfilePayload,
} from "../types/common";

const toTrimmedString = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const normalizeAuthResponseToAdminSession = (
  response: AuthResponse
): AdminSession => ({
  token: toTrimmedString(response.accessToken),
  tokenExpires: toTrimmedString(response.expiration) || undefined,
  name: toTrimmedString(response.user.name) || "Admin User",
  email: toTrimmedString(response.user.email).toLowerCase(),
  role: toTrimmedString(response.user.role) || "Admin",
  phone: toTrimmedString(response.user.phone),
});

export const transformResponseHandler = (
  response: AuthResponse
): TransformedAuthResponse => ({
  token: response.accessToken,
  expiresAt: new Date(response.expiration),
  isSuccess: response.success,
  message: response.message,
  errors: response.errors,
  user: {
    ...response.user,
    verified: response.user.isVerified,
  },
});

export type { AdminSession };
export { getStoredAdminUser, saveStoredAdminUser, subscribeToAdminAuth };

export const logoutAdmin = () => {
  clearStoredAdminUser();
};

export const updateStoredAdminProfile = (payload: UpdateAdminProfilePayload) => {
  const currentAdminUser = getStoredAdminUser();
  if (!currentAdminUser) {
    return;
  }

  saveStoredAdminUser({
    ...currentAdminUser,
    name: toTrimmedString(payload.name) || currentAdminUser.name,
    phone: toTrimmedString(payload.phone),
    role: toTrimmedString(payload.role) || currentAdminUser.role,
  });
};

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<AdminSession, LoginPayload>({
      query: ({ email, password }) => ({
        url: "/Auth/admin-login",
        method: "POST",
        body: {
          username: email.trim(),
          password,
        },
      }),
      transformResponse: normalizeAuthResponseToAdminSession,
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data: adminSession } = await queryFulfilled;
          saveStoredAdminUser(adminSession);
        } catch {
          // Error will be handled by the component
        }
      },
      invalidatesTags: ["AdminAuth"],
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: "/Auth/logout",
        method: "POST",
      }),
      async onQueryStarted(_args, _api) {
        clearStoredAdminUser();
      },
      invalidatesTags: ["AdminAuth"],
    }),

    forgotPassword: builder.mutation<{ success: boolean; message: string }, string>({
      query: (email) => ({
        url: "/Auth/forgot-password",
        method: "POST",
        body: { email },
      }),
    }),

    resetPassword: builder.mutation<{ success: boolean; message: string }, { token: string; newPassword: string }>({
      query: ({ token, newPassword }) => ({
        url: "/Auth/reset-password",
        method: "POST",
        body: { token, newPassword },
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} = authApi;